import { getPrimaryColor, JEJURO } from '@orcar/common-d2c';
import { useState } from 'react';
import styled from 'styled-components';

const MIN_USE = 1000;

type MileageAreaProps = {
  /** 보유 마일리지 */
  balance: number;
  /** 이번 결제에 사용 가능한 최대치 = min(잔액, 결제액의 50%) */
  maxUsable: number;
  /** 현재 적용된 사용액 */
  appliedMileage: number;
  onApply: (amount: number) => void;
  onClear: () => void;
};

const MileageArea = ({
  balance,
  maxUsable,
  appliedMileage,
  onApply,
  onClear,
}: MileageAreaProps) => {
  const [input, setInput] = useState('');

  const handleApply = () => {
    const requested = Number(input);
    if (!requested || requested < MIN_USE) {
      alert(
        `마일리지는 최소 ${MIN_USE.toLocaleString()}P부터 사용할 수 있어요.`,
      );
      return;
    }
    const amount = Math.min(requested, maxUsable);
    if (amount < MIN_USE) {
      alert('사용 가능한 마일리지가 부족해요.');
      return;
    }
    onApply(amount);
    setInput('');
  };

  const handleUseAll = () => {
    if (maxUsable < MIN_USE) {
      alert('사용 가능한 마일리지가 부족해요.');
      return;
    }
    onApply(maxUsable);
    setInput('');
  };

  return (
    <Wrapper>
      <Header>
        <Title>마일리지 사용</Title>
        <Balance>보유 {balance.toLocaleString()}P</Balance>
      </Header>

      {appliedMileage > 0 ? (
        <AppliedRow>
          <span>{appliedMileage.toLocaleString()}P 사용 중</span>
          <ClearButton type='button' onClick={onClear}>
            해제
          </ClearButton>
        </AppliedRow>
      ) : (
        <InputRow>
          <Input
            inputMode='numeric'
            placeholder={`최소 ${MIN_USE.toLocaleString()}P`}
            value={input}
            onChange={(e) => setInput(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <AllButton type='button' onClick={handleUseAll}>
            전액
          </AllButton>
          <ApplyButton type='button' onClick={handleApply}>
            적용
          </ApplyButton>
        </InputRow>
      )}

      <Hint>1,000P 이상, 결제금액의 최대 50%까지 사용할 수 있어요.</Hint>
    </Wrapper>
  );
};

export default MileageArea;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const Balance = styled.span`
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
  color: ${getPrimaryColor(JEJURO)};
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};
  border-radius: 8px;
  ${({ theme }) => theme.FONT.B2};

  &:focus {
    outline: none;
    border-color: ${getPrimaryColor(JEJURO)};
  }
`;

const AllButton = styled.button`
  flex-shrink: 0;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  cursor: pointer;
  ${({ theme }) => theme.FONT.B2};
  font-weight: 600;
`;

const ApplyButton = styled.button`
  flex-shrink: 0;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  cursor: pointer;
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
`;

const AppliedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  ${({ theme }) => theme.FONT.B2};
  font-weight: 600;
`;

const ClearButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  text-decoration: underline;
`;

const Hint = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;
